import _ from 'lodash';
import { logger } from '../../common/Services';
import CryptoJS from 'crypto-js'; // Import Google's crypto md5

/**
 * Each condition will be evaluated in order. Only the first condition who's key evaluates to `true` will be used. All subsequent keys that evaluate to true will be ignored. If it is a value it will be returned; if it is a function it will be executed
 * @param {object[]} conditions - an array of objects where the key can either be `true` or other and who's values can either be a function or a value
 * @returns {*} returns undefined if not found or value is a function, otherwise returns the value
 */
export function ifElse(conditions) {
  // Need to test for "true" the string rather than the boolean because all object keys are strings
  const result = _.find(conditions, (c) => Object.keys(c)[0] === 'true');

  if (!result) {
    return undefined;
  }

  const value = result[Object.keys(result)[0]];

  if (_.isFunction(value)) {
    value();
  } else {
    return value;
  }
}

export function valuesDeep(obj) {
  const values = [];
  _.forEach(obj, (value, key) => {
    if (_.isObject(value)) {
      values.push(...valuesDeep(value));
    } else {
      values.push(value);
    }
  });
  return values;
}

export function isImmutable(data) {
  return _.has(data, 'asMutable');
}

// helper method to make conditional addition of objects to arrays easier to read
export function insertIf(condition, ...elements) {
  return condition ? elements : [];
}

// http://2ality.com/2014/01/object-assign.html
// https://www.webreflection.co.uk/blog/2015/10/06/how-to-copy-objects-in-javascript
export function defineAssign(target) {
  for (
    var hOP = Object.prototype.hasOwnProperty,
      copy = function (key) {
        if (!hOP.call(target, key)) {
          Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(this, key));
        }
      },
      i = arguments.length;
    1 < i--;
    Object.keys(arguments[i]).forEach(copy, arguments[i])
  ) {}
  return target;
}

// will clean up an object such that null, undefined, plain object or function values are returned as zero length strings
const _cleanData = (data) => {
  return _.reduce(
    data,
    (c, v, key) => {
      if (process.env.NODE_ENV !== 'test') {
        logger.error('Trying to parameterize a function', _.isFunction(v));
      }
      return _.extend(c, {
        [key]: _.isPlainObject(v) || _.isFunction(v) || v === null || v === undefined ? '' : v,
      });
    },
    {}
  );
};

// rip through an object and flatten any values that are an array into a comma seperated list
const _flattenValues = (data) => {
  return _.reduce(
    data,
    (c, v, key) => {
      return _.extend(c, {
        [key]: _.isArray(v) ? v.join(',') : v,
      });
    },
    {}
  );
};

const _encodeAndJoin = (data) =>
  _.map(data, (value, key) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');

export const objectToParamsString = (data) => _encodeAndJoin(_flattenValues(_cleanData(data)));
// hash function from http://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript-jquery
export const stringToHash = (string) => {
  let hash = 0;
  _.forEach(string, (char, i) => {
    hash = (hash << 5) - hash + string.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  });
  return hash;
};

// memoized utility function to simplify setting up tab toggle behaviour
export function createTabToggle(items, initialIndex = 0) {
  let _currentIndex = initialIndex;

  return function toggle() {
    const nextIndex = (_currentIndex + 1) % 2;
    _currentIndex = nextIndex;
    return items[nextIndex];
  };
}

/**
 * create a unique id
 * @example 03122D69-558E-47BD-A5FD-57551793A6E3
 */
export function uniqueId() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16).toUpperCase()
  );
}

/**
 * utility object containing constants and function that helps creat unique identifiers for polls/surveys/activity feed. Modelled after the ObjectIdUtils.groovy in corelib
 */
export const ObjectIdUtils = {
  POLL_PREFIX:     'po',
  SURVEY_PREFIX:   'su',
  ACTIVITY_PREFIX: 'ac',
  createUUID:      (typePrefix) => `${typePrefix}-${CryptoJS.MD5(uniqueId()).toString()}`,
};

export const QUILL_EMPTY_CONTAINER_HTML = '<p><br></p>';
